import { useState } from 'react'
import { Notice } from '../Toast'
import { Link, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addGrant, closeApplication, extendAttempt, generateReports, getApplication, getAssessments, getPlans, overrideMode, reportUrl, resendInvite,
} from '../api/endpoints'
import type { ApplicationDetail } from '../api/types'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { ErrorNotice, ReasonDialog, StatusBadge } from '../components'
import { formatDateTime, num } from '../lib/format'

type Action = null | { kind: 'close'; status: 'WITHDRAWN' | 'DISQUALIFIED' } | { kind: 'grant' } | { kind: 'override' } | { kind: 'extend'; attemptId: number }

export default function ApplicationDetailPage() {
  const id = Number(useParams().id)
  const queryClient = useQueryClient()
  const { hasRole } = useAuth()
  const canAct = hasRole('ADMISSIONS_ADMIN', 'SUPER_ADMIN')
  const [action, setAction] = useState<Action>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const detail = useQuery({ queryKey: ['application', id], queryFn: () => getApplication(id) })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['application', id] })

  const resend = useMutation({ mutationFn: () => resendInvite(id), onSuccess: () => setNotice('Invite sent again.') })
  const openReport = useMutation({
    mutationFn: async () => {
      try {
        const r = await reportUrl(id)
        window.open(r.url, '_blank', 'noopener')
        return null
      } catch (e) {
        if (e instanceof ApiError && e.code === 'REPORT_NOT_READY') {
          if (!canAct) throw e
          const q = await generateReports({ applicationIds: [id] })
          return q.queued > 0 ? 'The report is being generated. Try again in a minute.' : 'The report is already being generated.'
        }
        throw e
      }
    },
    onSuccess: (m) => m && setNotice(m),
  })

  if (detail.isPending) return <p role="status">Loading…</p>
  if (detail.isError) return <ErrorNotice error={detail.error} />
  const d = detail.data
  const closed = ['WITHDRAWN', 'DISQUALIFIED'].includes(d.status)

  return (
    <div>
      <p><Link to="/applications">← Applications</Link></p>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div><h1>{d.profile.name}</h1><p className="muted">{d.applicationNo} · {d.program} · {d.cycle} · <StatusBadge status={d.status} /></p></div>
        <div className="row">
          {['SCORED', 'REPORT_READY'].includes(d.status) && <button type="button" onClick={() => openReport.mutate()} disabled={openReport.isPending}>{openReport.isPending ? 'Opening…' : 'View report'}</button>}
          {canAct && ['INVITED', 'REGISTERED'].includes(d.status) && <button type="button" onClick={() => resend.mutate()} disabled={resend.isPending}>Resend invite</button>}
          {canAct && !closed && <button type="button" onClick={() => setAction({ kind: 'grant' })}>Grant time or attempt</button>}
          {canAct && !closed && <button type="button" onClick={() => setAction({ kind: 'override' })}>Device override</button>}
          {canAct && !closed && <button type="button" onClick={() => setAction({ kind: 'close', status: 'WITHDRAWN' })}>Withdraw</button>}
          {canAct && !closed && <button type="button" className="danger" onClick={() => setAction({ kind: 'close', status: 'DISQUALIFIED' })}>Disqualify</button>}
        </div>
      </div>
      {notice && <Notice tone="ok">{notice}</Notice>}
      <ErrorNotice error={resend.error ?? openReport.error} />

      <div className="grid">
        <section className="card"><h2>Candidate</h2>
          <p>{d.profile.email}<br />{d.profile.mobile ?? '—'}</p>
          <p className="muted">{d.profile.school ?? '—'} · Grade {d.profile.grade ?? '—'} · {d.profile.board ?? '—'}<br />{d.profile.city ?? '—'}, {d.profile.state ?? '—'}<br />Born {d.profile.dateOfBirth ?? '—'}</p>
          {d.guardian && <p><b>Guardian</b><br />{d.guardian.name} · {d.guardian.email}</p>}
        </section>
        <section className="card"><h2>Combined score</h2>
          {d.overall ? (
            <p>VUSAT <b>{num(d.overall.vusatTotal)}</b> + MI <b>{num(d.overall.miMarks)}</b>{!d.overall.miCounts && ' (advisory)'} = <b>{num(d.overall.composite)}</b><br />
              <span className="muted">Program fit {num(d.overall.fitPct)}% · Band {d.overall.band ?? 'not set'} · Scored {formatDateTime(d.overall.scoredAt)}</span></p>
          ) : <p className="muted">Not scored yet.</p>}
        </section>
      </div>

      <section className="card"><h2>Registration</h2>
        <p className="muted">{d.registration.source === 'SELF' ? 'Registered through the campaign link' : 'Invited by an admin'} on {formatDateTime(d.registration.registeredAt)} · Email {d.registration.emailVerified ? 'confirmed' : 'not confirmed yet'}</p>
        {d.answers.length === 0 ? <p className="muted">No answers recorded.</p> : (
          <dl className="answers">{d.answers.map((a, i) => (
            <div key={i} style={a.stillAsked ? undefined : { opacity: 0.7 }}>
              <dt>{a.group ? `${a.group}: ` : ''}{a.label}{!a.stillAsked && ' (no longer asked)'}</dt><dd>{a.value ?? <span className="muted">no answer</span>}</dd>
            </div>
          ))}</dl>
        )}
      </section>

      <section className="card"><h2>Consent record</h2>
        {d.consents.length === 0 ? <p className="muted">No consent recorded. The candidate cannot start tests.</p> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Given by</th><th>Name</th><th>Wording</th><th>Consented</th><th>IP address</th><th>Requested</th><th>State</th></tr></thead>
            <tbody>{d.consents.map((c, i) => (
              <tr key={i}><td>{c.givenBy.toLowerCase()}</td><td>{c.consenterName ?? '—'}</td><td>{c.version}</td><td>{formatDateTime(c.consentedAt)}</td><td>{c.ipAddress ?? '—'}</td><td>{formatDateTime(c.requestedAt)}</td>
                <td>{c.consentedAt ? <span className="badge ok">Given</span> : c.revokedAt ? <span className="badge bad">Revoked</span> : <span className="badge warn">Waiting</span>}</td></tr>
            ))}</tbody>
          </table></div>
        )}
      </section>

      {d.attempts.map((a) => (
        <section className="card" key={a.id}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h2>{a.assessment}</h2>
            <div className="row"><span className="badge">{a.status.replace('_', ' ').toLowerCase()}</span>{a.incomplete && <span className="badge bad">Incomplete: time ran out</span>}<span className={`badge ${a.securityLevel === 'REDUCED' ? 'warn' : ''}`}>{a.securityLevel.toLowerCase()} security</span>
              {canAct && a.status === 'IN_PROGRESS' && <button type="button" onClick={() => setAction({ kind: 'extend', attemptId: a.id })}>Extend time</button>}</div>
          </div>
          <p className="muted">Started {formatDateTime(a.startedAt)} · {a.submittedAt ? `Submitted ${formatDateTime(a.submittedAt)}` : `Deadline ${formatDateTime(a.deadlineAt)}`} · Devices {a.deviceClasses.join(', ').toLowerCase()} · {a.answered} answered
            {a.extraTimeSeconds > 0 && ` · +${a.extraTimeSeconds / 60} min allowed`}</p>
          {a.kind === 'APTITUDE' && a.total !== null && (
            <div className="table-wrap"><table>
              <thead><tr><th>Section</th><th className="num">Raw</th><th className="num">Marks</th><th className="num">Percentile</th></tr></thead>
              <tbody>
                {a.sections.map((s) => <tr key={s.section}><td>{s.section}</td><td className="num">{s.raw} / {s.maxRaw}</td><td className="num">{num(s.scaled)}</td><td className="num">{num(s.percentile, 2)}</td></tr>)}
                <tr><td><b>Total</b></td><td /><td className="num"><b>{num(a.total)}</b></td><td className="num"><b>{num(a.percentile, 2)}</b>{a.percentileFinal === false && ' (provisional)'}{a.cohortSize ? ` of ${a.cohortSize}` : ''}</td></tr>
              </tbody>
            </table></div>
          )}
          {a.intelligences.length > 0 && (
            <div className="table-wrap"><table>
              <thead><tr><th>Intelligence</th><th>Score</th><th className="num">%</th><th>Band</th></tr></thead>
              <tbody>{a.intelligences.map((i) => (
                <tr key={i.intelligence}><td>{i.intelligence}{i.fit && <> <span className="badge warn">fit</span></>}</td><td><div className="bar"><i style={{ width: `${i.pct}%` }} /></div></td><td className="num">{i.pct}</td><td>{i.band.toLowerCase()}</td></tr>
              ))}</tbody>
            </table></div>
          )}
          {a.scoreVersions > 1 && <p className="muted">Scored {a.scoreVersions} times; earlier scores are kept for audit.</p>}
          <details><summary>Timeline ({a.timeline.length} events)</summary>
            <ul className="timeline">{a.timeline.map((t, i) => <li key={i}><b>{formatDateTime(t.at)}</b> · {t.type.toLowerCase()}{t.detail ? <span className="muted"> · {t.detail}</span> : null}</li>)}</ul>
          </details>
        </section>
      ))}

      {d.overridesAndGrants.length > 0 && (
        <section className="card"><h2>Overrides and grants</h2>
          <ul className="timeline">{d.overridesAndGrants.map((n, i) => <li key={i}><b>{formatDateTime(n.at)}</b> · {n.kind.replace('_', ' ').toLowerCase()} · {n.text}</li>)}</ul>
        </section>
      )}

      {action && <ActionDialog action={action} detail={d} onClose={() => setAction(null)} onDone={(msg) => { setAction(null); setNotice(msg); void refresh() }} />}
    </div>
  )
}

function ActionDialog({ action, detail, onClose, onDone }: { action: NonNullable<Action>; detail: ApplicationDetail; onClose: () => void; onDone: (message: string) => void }) {
  const [versionId, setVersionId] = useState('')
  const [mode, setMode] = useState('MOBILE_ALLOWED')
  const [attemptsExtra, setAttemptsExtra] = useState('0')
  const [minutes, setMinutes] = useState('0')
  const plans = useQuery({ queryKey: ['plans', detail.cycleId], queryFn: () => getPlans(detail.cycleId), enabled: action.kind === 'grant' || action.kind === 'override' })
  const assessments = useQuery({ queryKey: ['assessments'], queryFn: getAssessments, enabled: action.kind === 'grant' || action.kind === 'override' })
  const names = new Map(assessments.data?.flatMap((a) => a.versions.map((v) => [v.id, `${a.name} (v${v.versionNo})`] as const)) ?? [])
  const choices = (plans.data ?? []).filter((p) => p.programId === detail.programId)

  const run = useMutation({
    mutationFn: async (reason: string) => {
      switch (action.kind) {
        case 'close': await closeApplication(detail.id, action.status, reason); return action.status === 'DISQUALIFIED' ? 'Candidate disqualified.' : 'Candidate withdrawn.'
        case 'extend': await extendAttempt(action.attemptId, Number(minutes) * 60, reason); return `Added ${minutes} minutes.`
        case 'grant': await addGrant(detail.id, Number(versionId), Number(attemptsExtra), Number(minutes) * 60, reason); return 'Grant recorded; it applies when the candidate next starts.'
        case 'override': await overrideMode(detail.id, Number(versionId), mode, reason); return 'Device override recorded.'
      }
    },
    onSuccess: onDone,
  })
  const picker = (
    <label className="field">Assessment
      <select required value={versionId} onChange={(e) => setVersionId(e.target.value)}>
        <option value="" disabled>Choose…</option>
        {choices.map((p) => <option key={p.id} value={p.assessmentVersionId}>{names.get(p.assessmentVersionId) ?? `Version ${p.assessmentVersionId}`}</option>)}
      </select>
    </label>
  )
  const titles = { close: action.kind === 'close' && action.status === 'DISQUALIFIED' ? 'Disqualify this candidate?' : 'Withdraw this application?', grant: 'Grant extra time or an attempt', override: 'Allow another device type', extend: 'Extend the running test' }
  const ready = action.kind === 'close' || action.kind === 'extend' ? action.kind === 'close' || Number(minutes) > 0 : versionId !== ''
  return (
    <ReasonDialog title={titles[action.kind]} danger={action.kind === 'close'} confirmLabel={action.kind === 'close' ? (action.status === 'DISQUALIFIED' ? 'Disqualify' : 'Withdraw') : 'Save'}
      description={action.kind === 'close' ? 'Any test in progress is stopped and the candidate cannot continue. This is recorded in the audit log.' : undefined}
      busy={run.isPending || !ready} error={run.error} onCancel={onClose} onConfirm={(r) => run.mutate(r)}>
      {action.kind === 'grant' && (<>{picker}
        <div className="row"><label className="field">Extra attempts<input type="number" min={0} value={attemptsExtra} onChange={(e) => setAttemptsExtra(e.target.value)} /></label>
          <label className="field">Extra minutes<input type="number" min={0} max={360} value={minutes} onChange={(e) => setMinutes(e.target.value)} /></label></div></>)}
      {action.kind === 'override' && (<>{picker}
        <label className="field">Allow<select value={mode} onChange={(e) => setMode(e.target.value)}><option value="MOBILE_ALLOWED">Phones and tablets as well as computers</option><option value="MOBILE_FIRST">Phones and tablets (reduced security)</option><option value="SECURE_DESKTOP">Computers only</option></select></label></>)}
      {action.kind === 'extend' && <label className="field">Minutes to add (up to 60)<input type="number" min={1} max={60} value={minutes} onChange={(e) => setMinutes(e.target.value)} /></label>}
    </ReasonDialog>
  )
}
